import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Mail, Clock, User, FileText, Code } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';

function AliasDetailPage() {
  const { aliasId } = useParams();
  const navigate = useNavigate();
  const [alias, setAlias] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [viewMode, setViewMode] = useState('html');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [aliasId]);

  const fetchData = async () => {
    try {
      const [aliasRes, emailsRes] = await Promise.all([
        axiosInstance.get(`/aliases/${aliasId}`),
        axiosInstance.get(`/aliases/${aliasId}/emails`),
      ]);
      setAlias(aliasRes.data);
      setEmails(emailsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <p className="text-slate-600">Chargement...</p>
      </div>
    );
  }

  if (!alias) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Alias introuvable</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="btn btn-secondary flex items-center space-x-2"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Retour</span>
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-800">{alias.alias_email}</h1>
              <p className="text-sm text-slate-600">
                {alias.email_count} email(s) reçu(s)
              </p>
            </div>
            <span
              className={`badge ${
                alias.status === 'active' ? 'badge-active' : 'badge-expired'
              }`}
            >
              {alias.status === 'active' ? 'Actif' : 'Expiré'}
            </span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Email List */}
          <div className="lg:col-span-1">
            <div className="card mb-4">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center space-x-2">
                <Mail className="w-5 h-5" />
                <span>Emails reçus</span>
              </h2>
              {emails.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  Aucun email reçu pour cet alias
                </p>
              ) : (
                <div className="space-y-2" data-testid="emails-list">
                  {emails.map((email) => (
                    <div
                      key={email.id}
                      onClick={() => setSelectedEmail(email)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedEmail?.id === email.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`email-item-${email.id}`}
                    >
                      <p className="text-sm font-medium text-slate-800 mb-1 truncate">
                        {email.subject || '(Sans sujet)'}
                      </p>
                      <p className="text-xs text-slate-600 mb-1 truncate">
                        De: {email.sender}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDistanceToNow(new Date(email.received_at), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Alias Info */}
            <div className="card">
              <h3 className="font-semibold text-slate-800 mb-3">Informations</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-start space-x-2">
                  <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-600">Créé le</p>
                    <p className="text-slate-800">
                      {format(new Date(alias.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                    </p>
                  </div>
                </div>
                {alias.last_received_at && (
                  <div className="flex items-start space-x-2">
                    <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-600">Dernier email</p>
                      <p className="text-slate-800">
                        {format(new Date(alias.last_received_at), 'dd/MM/yyyy HH:mm', {
                          locale: fr,
                        })}
                      </p>
                    </div>
                  </div>
                )}
                {alias.expires_at && (
                  <div className="flex items-start space-x-2">
                    <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-600">Expire le</p>
                      <p className="text-slate-800">
                        {format(new Date(alias.expires_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                      </p>
                    </div>
                  </div>
                )}
                {!alias.expires_at && (
                  <div className="flex items-start space-x-2">
                    <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-slate-600">Expiration</p>
                      <p className="text-slate-800 font-medium text-indigo-600">Permanent</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Email Detail */}
          <div className="lg:col-span-2">
            {selectedEmail ? (
              <div className="card" data-testid="email-detail">
                <div className="border-b border-slate-200 pb-4 mb-4">
                  <h2 className="text-xl font-bold text-slate-800 mb-3">
                    {selectedEmail.subject || '(Sans sujet)'}
                  </h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start space-x-2">
                      <User className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <span className="text-slate-600">De: </span>
                        <span className="text-slate-800">{selectedEmail.sender}</span>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <span className="text-slate-600">Reçu: </span>
                        <span className="text-slate-800">
                          {format(new Date(selectedEmail.received_at), 'dd/MM/yyyy à HH:mm', {
                            locale: fr,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex space-x-2 mb-4">
                  <button
                    onClick={() => setViewMode('html')}
                    className={`btn ${
                      viewMode === 'html' ? 'btn-primary' : 'btn-secondary'
                    } flex items-center space-x-2`}
                    data-testid="view-html-btn"
                  >
                    <FileText className="w-4 h-4" />
                    <span>HTML</span>
                  </button>
                  <button
                    onClick={() => setViewMode('text')}
                    className={`btn ${
                      viewMode === 'text' ? 'btn-primary' : 'btn-secondary'
                    } flex items-center space-x-2`}
                    data-testid="view-text-btn"
                  >
                    <Code className="w-4 h-4" />
                    <span>Texte</span>
                  </button>
                </div>

                {/* Email Body */}
                <div className="bg-slate-50 rounded-lg p-4">
                  {viewMode === 'html' && selectedEmail.body_html ? (
                    <iframe
                      srcDoc={selectedEmail.body_html}
                      className="w-full min-h-[400px] bg-white rounded border border-slate-200"
                      title="Email content"
                      sandbox="allow-same-origin"
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">
                      {selectedEmail.body_text || 'Aucun contenu texte disponible'}
                    </pre>
                  )}
                </div>
              </div>
            ) : (
              <div className="card text-center py-12">
                <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Sélectionnez un email pour voir son contenu</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default AliasDetailPage;
